'use client';

/**
 * VendorChat - Vendor-level message view and WhatsApp compose
 *
 * Shows conversation history with a vendor across all RFQs/POs.
 * Pre-loads vendor contacts for quick messaging.
 *
 * @component_role Vendor-level Chat
 * @business_rule Shows all conversations with this vendor across all projects/RFQs.
 *   Useful for checking past negotiations or escalation history. Send WhatsApp
 *   directly to vendor contacts (from WORKS_FOR relationship).
 * @user_outcome Full vendor relationship history - see how past interactions went
 *   before starting new negotiations. Reference old agreements quickly.
 * @integration_point klyde_edge /neo/graph/explore/vendor, /messaging/whatsapp/send-to-contact
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import { MessageSquare, Factory } from 'lucide-react';
import { toast } from 'sonner';
import { useVendorExplore, useVendorContacts, useSendWhatsApp } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { ContactSummary, WhatsAppMediaAttachment } from '@/lib/api/types';

// Shared components
import { LoadingState, EmptyState } from './shared';
import { ContactList } from './contact-selector';
import { WhatsAppCompose } from './whatsapp-compose';

// ============================================================
// Sub-Components
// ============================================================

interface VendorHeaderProps {
  vendorName: string;
  vendorEmail?: string | null;
}

function VendorHeader({ vendorName, vendorEmail }: VendorHeaderProps) {
  return (
    <div className="p-3 border-b border-panel-border shrink-0">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">VENDOR MESSAGES</h4>
      <div className="p-2 rounded-lg bg-card border border-border">
        <div className="flex items-center gap-2">
          <Factory className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{vendorName}</span>
        </div>
        {vendorEmail && (
          <div className="text-xs text-muted-foreground mt-1">{vendorEmail}</div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

interface VendorChatProps {
  vendorId: string;
  projectId?: string | null;
}

export function VendorChat({ vendorId, projectId }: VendorChatProps) {
  const [selectedContact, setSelectedContact] = useState<ContactSummary | null>(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [attachments, setAttachments] = useState<WhatsAppMediaAttachment[]>([]);

  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const vendorContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_contacts'));
  const sendWhatsApp = useSendWhatsApp(sendWhatsAppStrategy!);

  // Fetch vendor data
  const { data: vendorData, isLoading: vendorLoading } = useVendorExplore(vendorExploreStrategy!, {
    vendor_id: vendorId,
    include_products: false,
    max_items: 1,
  });

  const vendor = vendorData?.vendor;

  // Fetch vendor contacts
  const { data: contactsData, isLoading: contactsLoading } = useVendorContacts(vendorContactsStrategy!, { vendor_id: vendorId });

  const contacts = contactsData?.contacts || [];

  // Loading state
  if (vendorLoading) {
    return <LoadingState message="Loading vendor..." />;
  }

  // Handlers
  const handleSendWhatsApp = async () => {
    if (!vendor?.odoo_id) {
      toast.error('Cannot send message', { description: 'Vendor details not loaded' });
      return;
    }

    if (!selectedContact?.phone) {
      toast.error('No phone number', { description: 'Please select a contact with a phone number' });
      return;
    }

    sendWhatsApp.mutate(
      {
        to: selectedContact.phone,
        message: whatsappMessage,
        context_model: 'res.partner',
        context_id: vendor.odoo_id,
        media: attachments.length > 0 ? attachments : undefined,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success('WhatsApp sent', { description: `Sent to ${selectedContact.name}` });
            setWhatsappMessage('');
            setAttachments([]);
          } else {
            toast.error('Failed to send', { description: data.whatsapp_error || 'Unknown error' });
          }
        },
        onError: (err) => {
          toast.error('Send failed', { description: (err as Error).message });
        },
      }
    );
  };

  const canSend =
    !!selectedContact?.phone &&
    (!!whatsappMessage.trim() || attachments.length > 0) &&
    !sendWhatsApp.isPending;
  const recipientLabel = selectedContact ? `Send to ${selectedContact.name}` : 'Select a contact above';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <VendorHeader
        vendorName={vendor?.name || vendorId}
        vendorEmail={vendor?.email}
      />

      {/* Contacts */}
      <div className="p-3 border-b border-panel-border">
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">CONTACTS</h4>
        <ContactList
          contacts={contacts}
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
          isLoading={contactsLoading}
          maxDisplay={5}
        />
      </div>

      {/* Message history placeholder */}
      <div className="flex-1 overflow-y-auto p-3">
        <EmptyState
          icon={MessageSquare}
          title="Vendor message history coming soon"
          subtitle="Select a contact below to send a WhatsApp"
        />
      </div>

      {/* WhatsApp Compose */}
      <WhatsAppCompose
        message={whatsappMessage}
        onMessageChange={setWhatsappMessage}
        onSend={handleSendWhatsApp}
        canSend={canSend}
        isSending={sendWhatsApp.isPending}
        recipientLabel={recipientLabel}
        placeholder={selectedContact ? 'Type a message...' : 'Select a contact first...'}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
      />
    </div>
  );
}
